/**
 * cancelAppointmentAsVisitor: 閲覧ユーザー自身による商談キャンセル。
 * US-D2-11、FR-Y8, NFR-AUTH3。BR-U6-2/BR-U6-5/BR-U6-11。
 */

import { relayClient } from "../core/relayClient";
import { classifyError, logClientWarning } from "../core/errorHandler";
import { isLoggedIn, setLoggedIn } from "../core/authState";
import { trackAppointmentCancel } from "../w3-tracking/trackPageEvent";

export interface CancelAppointmentResult {
  success: boolean;
  requiresLogin: boolean;
  deadlineExceeded: boolean;
}

/**
 * 2026-08-26新設: キャンセル可否の事前判定結果（BR-U2-5, isCancellability API参照）。
 * `cancellable=null`は判定不能（未ログイン・ネットワークエラー等）を意味し、
 * 呼び出し元は通常のキャンセル確認モーダルへフォールバックする。
 */
export interface CancellabilityResult {
  cancellable: boolean | null;
  reason: string | null;
}

/**
 * 2026-08-26新設: AppointmentCancelViewのページ表示時に呼ぶ、キャンセル可否の事前判定。
 * 状態変更を伴わない読み取り専用（BR-U2-5）。押下前に判定し、押下後のエラー待ちにしない。
 */
export async function isCancellable(appointmentId: string): Promise<CancellabilityResult> {
  if (!isLoggedIn()) {
    // ログイン要求フローに進むため判定不要（renderAppointmentCancelModalのログイン分岐が先に処理する）
    return { cancellable: null, reason: null };
  }

  const response = await relayClient.getCancellability<{ cancellable: boolean; reason: string | null }>({
    appointmentId,
  });

  // 中継エンドポイントのレスポンスヘッダーでログイン状態を反映する（セッション期限切れ検出）。
  if (response.loggedIn !== null) {
    setLoggedIn(response.loggedIn);
  }

  if (!response.ok || response.data === null) {
    // ネットワークエラー等。判定不能として通常のキャンセル確認モーダルにフォールバックする
    // （押下時にcancelAppointmentAsVisitorが最終判定するため機能停止にはならない）。
    logClientWarning("isCancellable failed, falling back to normal flow");
    return { cancellable: null, reason: null };
  }

  return { cancellable: response.data.cancellable, reason: response.data.reason };
}

export interface CancelAnalyticsContext {
  /** 出展企業root_id（テナント境界確定、NFR-A3。呼び出し元がアポ詳細から渡す）。 */
  companyRootId?: number | null;
  /** キャンセル操作の発生経路（TOP_PAGE_MODAL/WIDGET）。 */
  cancelEntryPoint?: "TOP_PAGE_MODAL" | "WIDGET";
}

export async function cancelAppointmentAsVisitor(
  appointmentId: string,
  reason?: string,
  analyticsCtx: CancelAnalyticsContext = {}
): Promise<CancelAppointmentResult> {
  const response = await relayClient.cancelAppointment({ appointmentId, reason });

  // 中継エンドポイントのレスポンスヘッダーでログイン状態を反映する。
  // トップページ常時埋め込み時はfetchScenarioが呼ばれないため、ここが唯一のログイン状態取得経路。
  if (response.loggedIn !== null) {
    setLoggedIn(response.loggedIn);
  }

  if (response.status === 401) {
    return { success: false, requiresLogin: true, deadlineExceeded: false };
  }

  if (!response.ok) {
    const classified = classifyError(response);
    const deadlineExceeded = classified.businessErrorCode === "CANCELLATION_DEADLINE_EXCEEDED";
    logClientWarning(`cancelAppointmentAsVisitor failed: ${classified.kind}`);
    return { success: false, requiresLogin: false, deadlineExceeded };
  }

  // 効果分析ログ（イベントK、BR-AN-1/BR-AN-14 §5）: 閲覧ユーザー起点のキャンセル成功のみを発火する
  // （失敗・期限超過は対象外）。appointmentId・companyRootId（テナント境界、NFR-A3）・cancelEntryPoint を
  // 呼び出し元から受け取り載せる（2026-09-29 レビュー反映: companyRootId 欠落で保存されない不具合を修正）。
  // cancelReason（自由記述）は PII 混入を避けるため送らない（2026-09-29確定。理由が必要なら
  // appointmentId から spica-api/DB を参照する）。
  trackAppointmentCancel({ appointmentId, ...analyticsCtx });

  return { success: true, requiresLogin: false, deadlineExceeded: false };
}

/**
 * BR-U6-11: トップページアクセス時にcancel_appointment_idパラメータの有無を検知する。
 */
export function detectCancelModalTrigger(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get("cancel_appointment_id");
}
